import { cn } from '@/lib/utils';

export interface ProgressBarProps {
  /** 0-100. Anything outside is clamped rather than drawn off the end. */
  value: number;
  /** What the bar is measuring, for screen readers. */
  label: string;
  /** Shown to the right of the label, e.g. "32/38 (84%)". */
  caption?: string;
  /** Hides the label row entirely — for a bar inside a dense table cell. */
  hideLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * A filled bar with an accessible label.
 *
 * Built once here because three screens draw one — activities, homework and the
 * attendance summary — and each had its own div with its own height and its own
 * rounding (CLAUDE.md §3). The fill is the only inline style in the product's
 * UI kit and cannot be a Tailwind class: the width is a runtime number, and
 * `w-[${pct}%]` is a class the compiler never sees and so never generates.
 */
export function ProgressBar({
  value,
  label,
  caption,
  hideLabel = false,
  size = 'md',
  className,
}: ProgressBarProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {!hideLabel && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-ink-2">{label}</span>
          {caption && <span className="font-[560] text-accent">{caption}</span>}
        </div>
      )}

      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={hideLabel ? label : undefined}
        aria-valuetext={caption ?? `${pct}%`}
        className={cn(
          'w-full overflow-hidden rounded-pill border border-line bg-surface-2',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        <div
          className="h-full rounded-pill bg-accent transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
